import React, { useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tr from "date-fns/locale/tr"; // Türkçe dil desteği
import enUS from "date-fns/locale/en-US"; // İngilizce dil desteği eklendi
import axios from "axios";
import { BsCalendar } from "react-icons/bs";

// Dilleri kaydediyoruz
registerLocale("tr", tr);
registerLocale("en", enUS);

const SingleRoomReservation = ({ roomId }) => {
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState("tr"); // Aktif dil için state
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });

  // --- ÇEVİRİ METİNLERİ (SÖZLÜK) ---
  const t = {
    tr: {
      title: "Rezervasyon Talep Et",
      checkIn: "Giriş Tarihi",
      checkOut: "Çıkış Tarihi",
      selectDate: "Tarih Seçiniz",
      name: "Ad Soyad",
      phone: "Telefon (5XX...)",
      email: "E-posta Adresi",
      buttonText: "REZERVASYON YAP",
      buttonLoading: "Gönderiliyor...",
      footerInfo: "*Talebiniz onaylandığında size dönüş yapılacaktır.",
      alertFillAll: "Lütfen tüm alanları doldurunuz.",
      alertBlocked: "Seçtiğiniz tarih aralığında odamız maalesef dolu. Lütfen başka tarih seçiniz.",
      alertSuccess: "Rezervasyon talebiniz alındı! Sizi arayarak onaylayacağız."
    },
    en: {
      title: "Request a Reservation",
      checkIn: "Check-in Date",
      checkOut: "Check-out Date",
      selectDate: "Select a Date",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email Address",
      buttonText: "MAKE A RESERVATION",
      buttonLoading: "Sending...",
      footerInfo: "*We will contact you once your request is approved.",
      alertFillAll: "Please fill in all fields.",
      alertBlocked: "The room is unfortunately booked for the selected dates. Please choose different dates.",
      alertSuccess: "Your reservation request has been received! We will call you to confirm."
    }
  };

  // --- YARDIMCI FONKSİYON ---
  const formatDateToString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 1. Dil Seçimini ve Backend'den Dolu Günleri Çek
  useEffect(() => {
    // Çerezden dili kontrol et
    const isEnglish = document.cookie.includes("googtrans=/tr/en");
    if (isEnglish) {
      setLang("en");
    } else {
      setLang("tr");
    }

    // Takvim verisini çek
    const fetchBlockedDates = async () => {
      try {
        const res = await axios.get(`https://api.pinkhomebalat.com/api/room-availability/${roomId}`);
        const dates = res.data.map((dateStr) => {
             const [year, month, day] = dateStr.split("-").map(Number);
             return new Date(year, month - 1, day);
        });
        setBlockedDates(dates);
      } catch (err) {
        console.error("Takvim verisi alınamadı", err);
      }
    };

    if (roomId) fetchBlockedDates();
  }, [roomId]);

  // Input Değişikliklerini Yakala
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Rezervasyon Gönder
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checkInDate || !checkOutDate || !formData.name || !formData.email || !formData.phone) {
      alert(t[lang].alertFillAll); // Dinamik uyarı mesajı
      return;
    }

    const isRangeBlocked = blockedDates.some(date => 
        date > checkInDate && date < checkOutDate
    );

    if (isRangeBlocked) {
        alert(t[lang].alertBlocked); // Dinamik uyarı mesajı
        return;
    }

    setLoading(true);

    try {
      const formattedCheckIn = formatDateToString(checkInDate);
      const formattedCheckOut = formatDateToString(checkOutDate);

      const res = await axios.post("https://api.pinkhomebalat.com/api/book-room", {
        roomId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        checkIn: formattedCheckIn,
        checkOut: formattedCheckOut,
      });

      if (res.data.success) {
        alert(t[lang].alertSuccess); // Dinamik başarı mesajı
        
        setCheckInDate(null);
        setCheckOutDate(null);
        setFormData({ name: "", email: "", phone: "" });
        
        window.location.reload();
      }
    } catch (err) {
      // API hatası gelirse onu göster, yoksa varsayılan hata mesajını ver
      alert("Error: " + (err.response?.data?.error || "An error occurred."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-xl border border-accent/20">
      <h3 className="h3 text-center mb-6">{t[lang].title}</h3>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
        {/* --- TAKVİM KISMI --- */}
        <div className="flex flex-col gap-y-2">
            <label className="text-sm font-semibold">{t[lang].checkIn}</label>
            <div className="relative flex items-center h-[50px] border border-gray-300 rounded-md px-2 bg-gray-50">
            <div className="absolute z-10 left-3 text-accent">
                <BsCalendar />
            </div>
            <DatePicker
                className="w-full h-full pl-8 bg-transparent outline-none cursor-pointer text-gray-700"
                selected={checkInDate}
                onChange={(date) => setCheckInDate(date)}
                placeholderText={t[lang].selectDate}
                locale={lang} // Dinamik Takvim Dili (en veya tr)
                minDate={new Date()}
                excludeDates={blockedDates}
                dateFormat="dd/MM/yyyy"
                calendarClassName="notranslate"
            />
            </div>
        </div>

        <div className="flex flex-col gap-y-2">
            <label className="text-sm font-semibold">{t[lang].checkOut}</label>
            <div className="relative flex items-center h-[50px] border border-gray-300 rounded-md px-2 bg-gray-50">
            <div className="absolute z-10 left-3 text-accent">
                <BsCalendar />
            </div>
            <DatePicker
                className="w-full h-full pl-8 bg-transparent outline-none cursor-pointer text-gray-700"
                selected={checkOutDate}
                onChange={(date) => setCheckOutDate(date)}
                placeholderText={t[lang].selectDate}
                locale={lang} // Dinamik Takvim Dili (en veya tr)
                minDate={checkInDate || new Date()}
                excludeDates={blockedDates}
                dateFormat="dd/MM/yyyy"
                disabled={!checkInDate}
                calendarClassName="notranslate"
            />
            </div>
        </div>

        {/* --- KİŞİSEL BİLGİLER --- */}
        <div className="flex flex-col gap-y-2 mt-2">
            <input 
                type="text" 
                name="name"
                placeholder={t[lang].name} 
                value={formData.name}
                onChange={handleChange}
                className="border border-gray-300 h-[50px] px-4 rounded-md outline-none focus:border-accent transition"
                required
            />
            <input 
                type="text" 
                name="phone"
                placeholder={t[lang].phone} 
                value={formData.phone}
                onChange={handleChange}
                className="border border-gray-300 h-[50px] px-4 rounded-md outline-none focus:border-accent transition"
                required
            />
            <input 
                type="email" 
                name="email"
                placeholder={t[lang].email} 
                value={formData.email}
                onChange={handleChange}
                className="border border-gray-300 h-[50px] px-4 rounded-md outline-none focus:border-accent transition"
                required
            />
        </div>

        <button 
            type="submit" 
            disabled={loading}
            className="btn btn-secondary w-full py-4 mt-2 shadow-md hover:shadow-lg transition-all"
        >
            {loading ? t[lang].buttonLoading : t[lang].buttonText}
        </button>

        <p className="text-xs text-gray-500 text-center mt-2">
            {t[lang].footerInfo}
        </p>
      </form>
    </div>
  );
};

export default SingleRoomReservation;